import { useTranslation } from 'react-i18next'
import { EventHandler, useCallback, KeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { MonetaryVerificationsTableProps } from './MonetaryVerificationTable.types'
import {
  Badge,
  Box,
  Button,
  Flex,
  FilterMulti,
  Icon,
  SearchFilterList,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters
} from '@percent/lemonade'
import { usePartnerFilter, useStatusesFilter } from './hooks'
import { CountryCodeFlag } from '@percent/admin-dashboard/common/components/countryCodeFlag/CountryCodeFlag'
import { useFilterValue } from '@percent/admin-dashboard/common/hooks'
import { handleClickAsLink } from '@percent/admin-dashboard/common/utility/handleClickAsLink'
import { getDate } from '@percent/admin-dashboard/common/utility'
import styles from './MonetaryVerificationTable.module.scss'
import { statusMap } from '@percent/admin-dashboard/constants/statusMap'
import { ALL_STATUSES_QUERY_VALUE } from './hooks/statusUtils'

export function MonetaryVerificationsTable({
  monetaryVerifications,
  isLoading,
  totalResults,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams
}: Readonly<MonetaryVerificationsTableProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { statusesOptions, onStatusChange, selectedStatuses } = useStatusesFilter({ queryParams, setQueryParams })
  const {
    partnerOptions,
    selectedPartnerIds,
    selectedPartnerOptions,
    onPartnerChange,
    isPartnerFilterLoading,
    partnerSearch,
    handleOpenChange
  } = usePartnerFilter({ queryParams, setQueryParams })

  const [searchValue, setSearchValue] = useFilterValue(queryParams.organisationId ?? '', [queryParams.organisationId])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    setSearchValue(value)
  }

  const handleKeyPress: EventHandler<KeyboardEvent<HTMLInputElement>> = useCallback(
    event => {
      if (event.key === 'Enter') {
        const organisationId = (event.target as HTMLInputElement).value.trim()

        setQueryParams({
          organisationId: organisationId || undefined
        })
      }

      if (event.key === 'Escape') {
        setSearchValue('')
        setQueryParams({ organisationId: undefined })
      }
    },
    [setQueryParams, setSearchValue]
  )

  const handleClearSearch = useCallback(() => {
    setSearchValue('')
    setQueryParams({ organisationId: undefined })
  }, [setQueryParams, setSearchValue])

  const handleClearFilters = useCallback(() => {
    setSearchValue('')
    setQueryParams({
      organisationId: undefined,
      partnerIds: undefined,
      statuses: [ALL_STATUSES_QUERY_VALUE]
    })
  }, [setQueryParams, setSearchValue])

  const handleRequestSort = () => {
    setQueryParams({ direction: queryParams.direction === 'ASC' ? 'DESC' : 'ASC' })
  }

  const columns: TableColumn[] = [
    { id: 'country', content: t('table.country'), isSortable: false, props: { width: '10%' } },
    { id: 'organization', content: t('table.organization'), isSortable: false, props: { width: '40%' } },
    { id: 'partnerName', content: t('table.partnerName'), isSortable: false, props: { width: '25%' } },
    { id: 'status', content: t('table.status'), isSortable: false, props: { width: '10%' } },
    { id: 'requestDate', content: t('table.requestDate'), isSortable: true, props: { width: '15%' } }
  ]

  return (
    <TableProvider
      columns={columns}
      emptyTableTitle={t('table.emptyMonetaryVerifications')}
      illustration="no-results"
      isLoading={isLoading}
      totalResults={totalResults}
      sortByColumn="requestDate"
      sortDirection={queryParams.direction.toUpperCase() as TableColumnSortDirection}
      handleRequestSort={handleRequestSort}
    >
      <TableSearchAndFilters>
        <Flex align="center" gap="s" w="100%">
          <Box style={{ flex: 1 }}>
            <SearchInput
              value={searchValue ?? ''}
              onChange={handleSearchChange}
              onKeyDown={handleKeyPress}
              handleClearValue={handleClearSearch}
              clearButtonAriaLabel={t('table.clearOrganizationSearch')}
              placeholder={t('table.monetaryVerificationsSearchPlaceholder')}
            />
          </Box>
          <SearchFilterList
            label={t('typography.partner')}
            options={partnerOptions}
            selectedValues={selectedPartnerIds}
            selectedOptions={selectedPartnerOptions}
            onChange={onPartnerChange}
            isLoading={isPartnerFilterLoading}
            searchValue={partnerSearch.value}
            onSearchChange={partnerSearch.onChange}
            handleClearValue={partnerSearch.onClear}
            onKeyDown={partnerSearch.onKeyDown}
            onOpenChange={handleOpenChange}
            placeholder={t('typography.search')}
            clearButtonAriaLabel={t('table.clearPartnerSearch')}
            testId="partner-filter"
            badgeTestId="partner-filter-badge"
            placement="bottom-end"
          />
          <FilterMulti
            label={t('typography.status')}
            options={statusesOptions}
            selectedValues={selectedStatuses}
            onChange={onStatusChange}
            allOptionLabel={t('typography.all')}
            testId="status-filter"
          />
          <Button variant="tertiary" size="small" onClick={handleClearFilters}>
            {t('typography.clear')}
          </Button>
        </Flex>
      </TableSearchAndFilters>
      <Table>
        <TableBody>
          {monetaryVerifications?.map(monetaryVerification => {
            return (
              <TableRow
                key={monetaryVerification.id}
                data-testid="agent-verification-row"
                className={monetaryVerification.id ? styles.row : undefined}
                onClick={handleClickAsLink(`/monetary-verifications/${monetaryVerification.id}`, navigate)}
              >
                <TableCell>
                  <CountryCodeFlag countryCode={monetaryVerification.organisationDetails.countryCode} />
                </TableCell>
                <TableCell>{monetaryVerification.organisationDetails.name}</TableCell>
                <TableCell>{monetaryVerification.partner?.name}</TableCell>
                <TableCell className={styles.status}>
                  <Badge
                    variant={statusMap[monetaryVerification.status].variant}
                    icon={statusMap[monetaryVerification.status].icon}
                  >
                    {t(statusMap[monetaryVerification.status].text)}
                  </Badge>
                </TableCell>
                <TableCell>{getDate(monetaryVerification.createdAt)}</TableCell>
                <TableCell className={styles.rowIconForward}>
                  <Icon name="chevron-right" color="gray400" size={4} />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <TablePagination
        nextPage={nextPage}
        previousPage={previousPage}
        totalResultsText={t('table.results', { count: totalResults ?? 0 })}
      />
    </TableProvider>
  )
}
